import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useFronteggToken } from '../hooks/useFrontegg';
import './MyActivity.css';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

function formatDuration(ms) {
    if (!ms || ms <= 0) return '—';
    const sec = Math.floor(ms / 1000);
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    if (h) return `${h}h ${m}m`;
    if (m) return `${m}m ${s}s`;
    return `${s}s`;
}

function formatDate(v) {
    if (!v) return '—';
    return new Date(v).toLocaleString();
}

export default function MyActivity() {
    const token = useFronteggToken();
    const [tab, setTab] = useState('sessions');
    const [summary, setSummary] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [activity, setActivity] = useState([]);
    const [mcpSummary, setMcpSummary] = useState(null);
    const [mcpEvents, setMcpEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchAll = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        setError(null);
        try {
            const headers = { Authorization: `Bearer ${token}` };
            const [s, ss, aa, ms, me] = await Promise.all([
                axios.get(`${BASE_URL}/api/me/summary`, { headers }),
                axios.get(`${BASE_URL}/api/me/sessions?limit=50`, { headers }),
                axios.get(`${BASE_URL}/api/me/activity?limit=100`, { headers }),
                axios.get(`${BASE_URL}/api/me/mcp/summary`, { headers }),
                axios.get(`${BASE_URL}/api/me/mcp/events?limit=200`, { headers }),
            ]);
            setSummary(s.data);
            setSessions(ss.data.items || []);
            setActivity(aa.data.items || []);
            setMcpSummary(ms.data);
            setMcpEvents(me.data.items || []);
        } catch (err) {
            setError(err?.response?.data?.error || err.message);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    if (loading) return <div className="my-activity-page"><p>Loading…</p></div>;
    if (error) return <div className="my-activity-page"><p className="my-activity-error">{error}</p></div>;

    return (
        <div className="my-activity-page">
            <h1>My Activity</h1>
            {summary && (
                <div className="my-activity-summary">
                    <div className="my-activity-card">
                        <div className="my-activity-card-label">Total sessions</div>
                        <div className="my-activity-card-value">{summary.totalSessions}</div>
                    </div>
                    <div className="my-activity-card">
                        <div className="my-activity-card-label">Total time</div>
                        <div className="my-activity-card-value">{formatDuration(summary.totalDurationMs)}</div>
                    </div>
                    <div className="my-activity-card">
                        <div className="my-activity-card-label">Drafts created</div>
                        <div className="my-activity-card-value">{summary.drafts}</div>
                    </div>
                    <div className="my-activity-card">
                        <div className="my-activity-card-label">Searches</div>
                        <div className="my-activity-card-value">{summary.searches}</div>
                    </div>
                    <div className="my-activity-card">
                        <div className="my-activity-card-label">Translations</div>
                        <div className="my-activity-card-value">{summary.translations}</div>
                    </div>
                    <div className="my-activity-card">
                        <div className="my-activity-card-label">Publishes</div>
                        <div className="my-activity-card-value">{summary.publishes}</div>
                    </div>
                </div>
            )}

            <div className="my-activity-tabs">
                <button className={tab === 'sessions' ? 'active' : ''} onClick={() => setTab('sessions')}>Sessions</button>
                <button className={tab === 'activity' ? 'active' : ''} onClick={() => setTab('activity')}>Activity</button>
                <button className={tab === 'mcp' ? 'active' : ''} onClick={() => setTab('mcp')}>
                    MCP {mcpSummary?.totalCalls ? <span className="my-activity-tab-badge">{mcpSummary.totalCalls}</span> : null}
                </button>
            </div>

            {tab === 'sessions' && (
                <div className="my-activity-table-wrap">
                    <table className="my-activity-table">
                        <thead>
                            <tr>
                                <th>Started</th>
                                <th>Duration</th>
                                <th>Drafts</th>
                                <th>Searches</th>
                                <th>Publishes</th>
                                <th>Ended</th>
                                <th>IP</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sessions.map((s) => (
                                <tr key={s.sessionId}>
                                    <td>{formatDate(s.startedAt)}</td>
                                    <td>{formatDuration(s.durationMs)}</td>
                                    <td>{s.activityCounts?.brief_created || 0}</td>
                                    <td>{s.activityCounts?.search || 0}</td>
                                    <td>{s.activityCounts?.cms_publish || 0}</td>
                                    <td>{s.endedAt ? s.endReason || 'ended' : 'active'}</td>
                                    <td>{s.ipAddress || '—'}</td>
                                </tr>
                            ))}
                            {sessions.length === 0 && (
                                <tr><td colSpan={7} className="my-activity-empty">No sessions yet</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {tab === 'activity' && (
                <div className="my-activity-table-wrap">
                    <table className="my-activity-table">
                        <thead>
                            <tr>
                                <th>Time</th>
                                <th>Action</th>
                                <th>Details</th>
                            </tr>
                        </thead>
                        <tbody>
                            {activity.map((a) => (
                                <tr key={a._id}>
                                    <td>{formatDate(a.timestamp)}</td>
                                    <td><code>{a.action}</code></td>
                                    <td className="my-activity-meta">{a.metadata ? JSON.stringify(a.metadata) : ''}</td>
                                </tr>
                            ))}
                            {activity.length === 0 && (
                                <tr><td colSpan={3} className="my-activity-empty">No activity yet</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {tab === 'mcp' && (
                <>
                    <div className="my-activity-summary">
                        <div className="my-activity-card">
                            <div className="my-activity-card-label">MCP calls</div>
                            <div className="my-activity-card-value">{mcpSummary?.totalCalls || 0}</div>
                        </div>
                        <div className="my-activity-card">
                            <div className="my-activity-card-label">Success rate</div>
                            <div className="my-activity-card-value">
                                {mcpSummary && mcpSummary.totalCalls
                                    ? `${Math.round(mcpSummary.successRate * 100)}%`
                                    : '—'}
                            </div>
                        </div>
                        <div className="my-activity-card">
                            <div className="my-activity-card-label">Avg latency</div>
                            <div className="my-activity-card-value">
                                {mcpSummary?.avgLatencyMs ? `${mcpSummary.avgLatencyMs} ms` : '—'}
                            </div>
                        </div>
                        <div className="my-activity-card">
                            <div className="my-activity-card-label">Errors</div>
                            <div className="my-activity-card-value">{mcpSummary?.errors || 0}</div>
                        </div>
                    </div>

                    <div className="my-activity-table-wrap">
                        <table className="my-activity-table">
                            <thead>
                                <tr>
                                    <th>Time</th>
                                    <th>Tool</th>
                                    <th>Query</th>
                                    <th>Results</th>
                                    <th>Latency</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {mcpEvents.map((e) => (
                                    <tr key={e._id}>
                                        <td>{formatDate(e.timestamp)}</td>
                                        <td><code>{e.toolName}</code></td>
                                        <td className="my-activity-meta" title={e.query || ''}>{e.query || '—'}</td>
                                        <td>{e.resultCount ?? 0}</td>
                                        <td>{e.latencyMs != null ? `${e.latencyMs} ms` : '—'}</td>
                                        <td>{e.status || 'unknown'}</td>
                                    </tr>
                                ))}
                                {mcpEvents.length === 0 && (
                                    <tr><td colSpan={6} className="my-activity-empty">No MCP activity yet</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
}
